import { useCallback, useRef } from 'react';
import { useAiChatStore } from '../store/aiChat.store';

const SAMPLE_RATE = 16000;
const MIN_CHUNK_DURATION_MS = 50;
const MIN_CHUNK_SAMPLES = Math.floor((SAMPLE_RATE * MIN_CHUNK_DURATION_MS) / 1000);
const VOICE_ACTIVITY_RMS_THRESHOLD = 0.01;
const SILENCE_TIMEOUT_MS = 500;

type VoiceTranscriptWord = { confidence: number; end: number; start: number; text: string; word_is_final: boolean };

export type VoiceTranscriptionTurn = {
  end_of_turn: boolean;
  end_of_turn_confidence: number;
  turn_order: number;
  transcript: string;
  type: 'Turn';
  utterance: string;
  turn_is_formatted: boolean;
  words: VoiceTranscriptWord[];
};

export default function useAiVoiceTranscription({
  onTranscribe,
  onStopRecording,
}: {
  onTranscribe?: (turn: VoiceTranscriptionTurn) => void;
  onStopRecording?: () => void;
} = {}) {
  const socketRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
  const audioBufferQueueRef = useRef<Int16Array>(new Int16Array(0));
  const isSpeakingRef = useRef<boolean>(false);
  const lastAudioSentRef = useRef<number>(0);
  const silenceCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isVoiceRecording = useAiChatStore((state) => state.isVoiceRecording);
  const setIsVoiceRecording = useAiChatStore((state) => state.setIsVoiceRecording);

  const stopRecording = useCallback(() => {
    if (audioWorkletNodeRef.current) {
      audioWorkletNodeRef.current.disconnect();
      audioWorkletNodeRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.send(JSON.stringify({ type: 'Terminate' }));
      socketRef.current.close();
      socketRef.current = null;
    }

    if (silenceCheckIntervalRef.current) {
      clearInterval(silenceCheckIntervalRef.current);
      silenceCheckIntervalRef.current = null;
    }

    audioBufferQueueRef.current = new Int16Array(0);
    isSpeakingRef.current = false;
    lastAudioSentRef.current = 0;
    setIsVoiceRecording(false);
    onStopRecording?.();
  }, [onStopRecording, setIsVoiceRecording]);

  const startRecording = useCallback(async () => {
    const response = await fetch('/.redwood/functions/assemblyAiToken', { method: 'GET' });
    const { token } = await response.json();

    if (!token) {
      console.error('Failed to get token');
      return null;
    }

    const wsUrl = `${process.env.ASSEMBLY_AI_WEBSOCKET_API_URL}?sample_rate=${SAMPLE_RATE}&formatted_finals=true&token=${token}`;
    socketRef.current = new WebSocket(wsUrl);

    socketRef.current.onopen = async () => {
      console.log('WebSocket connection established');

      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioContextRef.current = new AudioContext({ sampleRate: SAMPLE_RATE });

      const source = audioContextRef.current.createMediaStreamSource(mediaStreamRef.current);

      await audioContextRef.current.audioWorklet.addModule('/audio-worklet.js');
      audioWorkletNodeRef.current = new AudioWorkletNode(audioContextRef.current, 'audio-processor');

      source.connect(audioWorkletNodeRef.current);
      audioWorkletNodeRef.current.connect(audioContextRef.current.destination);

      // Start silence check interval
      silenceCheckIntervalRef.current = setInterval(() => {
        if (!isSpeakingRef.current || !socketRef.current) return;

        const timeSinceLastAudio = Date.now() - lastAudioSentRef.current;

        if (timeSinceLastAudio > SILENCE_TIMEOUT_MS) {
          // Flush remaining buffer
          if (audioBufferQueueRef.current.length > 0) {
            let bufferToSend = audioBufferQueueRef.current;

            // Pad to minimum if needed to meet API requirements
            if (bufferToSend.length < MIN_CHUNK_SAMPLES) {
              const padded = new Int16Array(MIN_CHUNK_SAMPLES);
              padded.set(bufferToSend, 0);
              bufferToSend = padded;
            }

            if (socketRef.current?.readyState === WebSocket.OPEN) {
              socketRef.current.send(bufferToSend.buffer);
            }
          }

          audioBufferQueueRef.current = new Int16Array(0);
          isSpeakingRef.current = false;
          lastAudioSentRef.current = 0;
        }
      }, 10);

      audioWorkletNodeRef.current.port.onmessage = (event) => {
        if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) return;

        const currentBuffer = new Int16Array(event.data.audio_data);

        // Voice activity detection using RMS
        let sumSquares = 0;
        for (let i = 0; i < currentBuffer.length; i++) {
          const normalized = currentBuffer[i] / 32767;
          sumSquares += normalized * normalized;
        }
        const rms = Math.sqrt(sumSquares / currentBuffer.length);
        const hasVoiceActivity = rms > VOICE_ACTIVITY_RMS_THRESHOLD;

        // Skip silence before speech starts
        if (!hasVoiceActivity && !isSpeakingRef.current) {
          return;
        }

        // Update speaking state and timestamp only when voice is detected
        if (hasVoiceActivity) {
          isSpeakingRef.current = true;
          lastAudioSentRef.current = Date.now();
        }

        const merged = new Int16Array(audioBufferQueueRef.current.length + currentBuffer.length);
        merged.set(audioBufferQueueRef.current, 0);
        merged.set(currentBuffer, audioBufferQueueRef.current.length);
        audioBufferQueueRef.current = merged;

        const bufferDuration = (audioBufferQueueRef.current.length / SAMPLE_RATE) * 1000;

        if (bufferDuration >= MIN_CHUNK_DURATION_MS) {
          const bufferToSend = audioBufferQueueRef.current.subarray(0, MIN_CHUNK_SAMPLES);
          audioBufferQueueRef.current = audioBufferQueueRef.current.subarray(MIN_CHUNK_SAMPLES);

          socketRef.current.send(bufferToSend.buffer);
        }
      };
    };

    socketRef.current.onmessage = (event) => {
      const message = JSON.parse(event.data);

      if (message.type === 'Turn') onTranscribe?.(message as VoiceTranscriptionTurn);
    };

    socketRef.current.onerror = (err) => {
      console.error('WebSocket error:', err);
    };

    socketRef.current.onclose = (err) => {
      console.log('WebSocket closed', err);
      socketRef.current = null;
    };

    setIsVoiceRecording(true);

    return () => {
      stopRecording();
    };
  }, [stopRecording, onTranscribe, setIsVoiceRecording]);

  return { startRecording, stopRecording, isSpeakingRef, isVoiceRecording };
}
